import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Textarea } from 'components/ui/textarea';
import { StatusBadge } from 'components/StatusBadge';
import { MapPanel } from 'components/MapPanel';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { formatPrice, formatDate, formatDateTime, PARCEL_TYPES } from 'lib/format';
import { ArrowLeft, Package, MapPin, Calendar, Weight, AlertTriangle, ImageIcon, ImagePlus, MessageCircle, Edit3, Trash2, Star, BadgeCheck, X, Eye } from 'lucide-react';

const MAX_PHOTOS = 5;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 1200;
const MAX_PHOTO_DATA_URL_LENGTH = 450000;

const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_DECODE_ERROR'));
    image.src = String(reader.result || '');
  };
  reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
  reader.readAsDataURL(file);
});

const compressParcelPhoto = async (file) => {
  const image = await loadImage(file);
  let ratio = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('CANVAS_UNAVAILABLE');

  for (let pass = 0; pass < 5; pass += 1) {
    const width = Math.max(1, Math.round(image.width * ratio));
    const height = Math.max(1, Math.round(image.height * ratio));
    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);
    for (let quality = 0.84; quality >= 0.44; quality -= 0.08) {
      const output = canvas.toDataURL('image/jpeg', quality);
      if (output.length <= MAX_PHOTO_DATA_URL_LENGTH) return output;
    }
    ratio *= 0.8;
  }
  throw new Error('IMAGE_TOO_LARGE');
};

const buildDeliveryEditForm = (delivery) => ({
  title: delivery.title || '',
  description: delivery.description || '',
  desired_date: delivery.desired_date ? delivery.desired_date.slice(0, 10) : '',
  pickup_start_time: delivery.pickup_start_time ? delivery.pickup_start_time.slice(0, 16) : '',
  pickup_end_time: delivery.pickup_end_time ? delivery.pickup_end_time.slice(0, 16) : '',
  delivery_start_time: delivery.delivery_start_time ? delivery.delivery_start_time.slice(0, 16) : '',
  delivery_end_time: delivery.delivery_end_time ? delivery.delivery_end_time.slice(0, 16) : '',
  budget: delivery.budget || '',
  weight_kg: delivery.weight_kg || '',
  size: delivery.size || 'S',
  parcel_type: delivery.parcel_type || 'small',
  dimensions_cm: delivery.dimensions_cm || '',
  photos: delivery.photos || [],
  recipient_name: delivery.recipient_name || '',
  recipient_phone: delivery.recipient_phone || '',
  departure_address: delivery.departure_address || '',
  arrival_address: delivery.arrival_address || '',
  fragile: !!delivery.fragile,
  urgent: !!delivery.urgent,
});

export default function DeliveryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoEditOpenedFor = useRef('');
  const [delivery, setDelivery] = useState(null);
  const [senderProfile, setSenderProfile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [selectedTripId, setSelectedTripId] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [photosProcessing, setPhotosProcessing] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    api.get(`/deliveries/${id}`).then(r => {
      setDelivery(r.data);
    });
    api.get('/trips?mine=true').then(r => setTrips(r.data.filter(tripOption => tripOption.status === 'active'))).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!delivery?.sender_id) return setSenderProfile(null);
    api.get(`/users/${delivery.sender_id}`).then((r) => setSenderProfile(r.data)).catch(() => setSenderProfile(null));
  }, [delivery?.sender_id]);

  useEffect(() => {
    if (!delivery || !user || delivery.sender_id !== user.id || searchParams.get('edit') !== '1') return;
    if (autoEditOpenedFor.current === id || !['open', 'expired'].includes(delivery.status)) return;
    autoEditOpenedFor.current = id;
    setEditForm(buildDeliveryEditForm(delivery));
    setEditOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [delivery, id, searchParams, setSearchParams, user]);

  if (!delivery) return <DashboardShell><div>{t('deliveryDetail.loading')}</div></DashboardShell>;

  const isOwner = user?.id === delivery.sender_id;
  const typeLabel = (PARCEL_TYPES.find(parcelType => parcelType.value === delivery.parcel_type) || {}).label || delivery.parcel_type;
  const isInternational = delivery.departure_country !== delivery.arrival_country;

  const openEditDialog = () => {
    setEditForm(buildDeliveryEditForm(delivery));
    setEditOpen(true);
  };

  const addEditPhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    const remaining = MAX_PHOTOS - (editForm.photos || []).length;
    if (!remaining || !selectedFiles.length) return;
    if (selectedFiles.length > remaining) toast.error(t('deliveryDetail.photosMax').replace('{count}', String(MAX_PHOTOS)));

    setPhotosProcessing(true);
    try {
      const validFiles = selectedFiles.slice(0, remaining);
      if (validFiles.some((file) => !file.type.startsWith('image/') || file.size > MAX_UPLOAD_BYTES)) {
        toast.error(t('deliveryDetail.photoSize'));
      }
      const photos = await Promise.all(
        validFiles.filter((file) => file.type.startsWith('image/') && file.size <= MAX_UPLOAD_BYTES).map(compressParcelPhoto),
      );
      setEditForm((current) => ({ ...current, photos: [...(current.photos || []), ...photos] }));
    } catch (error) {
      toast.error(error.message === 'IMAGE_TOO_LARGE' ? t('deliveryDetail.photoTooLarge') : t('deliveryDetail.photoError'));
    } finally {
      setPhotosProcessing(false);
    }
  };

  const removeEditPhoto = (index) => setEditForm((current) => ({
    ...current,
    photos: (current.photos || []).filter((_, photoIndex) => photoIndex !== index),
  }));

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const reopening = delivery.status === 'expired';
    if (!editForm.budget || Number(editForm.budget) <= 0) {
      toast.error(t('deliveryDetail.budgetRequired'));
      return;
    }
    const appointmentWindows = [
      [t('deliveryDetail.pickup').toLowerCase(), editForm.pickup_start_time, editForm.pickup_end_time],
      [t('deliveryDetail.handoff').toLowerCase(), editForm.delivery_start_time, editForm.delivery_end_time],
    ];
    const invalidWindow = appointmentWindows.find(([, start, end]) => Boolean(start) !== Boolean(end) || (start && end && start >= end));
    if (invalidWindow) {
      toast.error(t('deliveryDetail.windowError').replace('{window}', invalidWindow[0]));
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        budget: Number(editForm.budget),
        weight_kg: editForm.weight_kg ? Number(editForm.weight_kg) : undefined,
      };
      const res = await api.patch(`/deliveries/${id}`, payload);
      setDelivery(res.data);
      toast.success(t(reopening ? 'deliveryDetail.reopened' : 'deliveryDetail.updated'));
      setEditOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('deliveryDetail.updateError')));
    } finally {
      setSavingEdit(false);
    }
  };

  const submitOffer = async () => {
    try {
      const mission = await api.post('/missions', {
        delivery_request_id: id,
        trip_id: selectedTripId || null,
        proposed_price: Number(offerPrice || delivery.budget),
        message: offerMessage,
      });
      toast.success(t('deliveryDetail.offerSent'));
      setOfferOpen(false);
      navigate(`/missions/${mission.data.id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('deliveryDetail.error')));
    }
  };

  const cancelDelivery = async () => {
    if (!window.confirm(t('deliveryDetail.cancelConfirm'))) return;
    try {
      await api.delete(`/deliveries/${id}`);
      toast.success(t('deliveryDetail.cancelled'));
      navigate('/deliveries');
    } catch { toast.error(t('deliveryDetail.cancelError')); }
  };

  const deleteDeliveryPermanently = async () => {
    if (!window.confirm(t('deliveryDetail.deleteParcel'))) return;
    try {
      await api.delete(`/deliveries/${id}/permanent`);
      toast.success(t('deliveryDetail.deleted'));
      navigate('/deliveries');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('deliveryDetail.error')));
    }
  };

  const isPublishedToday = delivery.created_at
    && new Date(delivery.created_at).toDateString() === new Date().toDateString();

  return (
    <DashboardShell>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft size={16} className="mr-1" /> {t('deliveryDetail.back')}
      </Button>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h1 className="text-2xl font-bold">{delivery.title}</h1>
                  <div className="text-sm text-muted-foreground">{typeLabel} • {delivery.size} • {delivery.weight_kg} kg</div>
                </div>
              {isOwner && Number.isFinite(delivery.views_30d) && (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-sm text-muted-foreground" data-testid="delivery-views-count">
                  <Eye size={15} aria-hidden="true" />
                  <span>{t('listingViews.last30Days').replace('{count}', String(delivery.views_30d))}</span>
                </div>
              )}
                <StatusBadge status={delivery.status} />
              </div>
              {delivery.description && <p className="text-sm text-foreground">{delivery.description}</p>}
              {delivery.fragile && <Badge variant="outline" className="mr-2 mt-3">{t('deliveryDetail.fragile')}</Badge>}
              {delivery.urgent && <Badge variant="outline" className="mt-3">{t('deliveryDetail.urgent')}</Badge>}
            </CardContent>
          </Card>

          {delivery.photos?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ImageIcon size={16} /> {t('deliveryDetail.photosTitle')}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {delivery.photos.map((photo, index) => (
                    <button key={photo} type="button" onClick={() => setSelectedPhoto({ src: photo, index })} className="aspect-square overflow-hidden rounded-md border bg-muted text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                      <img src={photo} alt={t('deliveryDetail.photoAlt').replace('{index}', String(index + 1))} className="h-full w-full object-cover transition-transform hover:scale-105" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
            <DialogContent className="max-w-4xl border-0 bg-transparent p-0 shadow-none sm:rounded-lg">
              <DialogTitle className="sr-only">{t('deliveryDetail.photosTitle')}</DialogTitle>
              <button
                type="button"
                onClick={() => setSelectedPhoto(null)}
                className="absolute right-2 top-2 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={t('deliveryDetail.closePhoto')}
              >
                <X size={18} />
              </button>
              {selectedPhoto && (
                <img
                  src={selectedPhoto.src}
                  alt={t('deliveryDetail.photoAlt').replace('{index}', String(selectedPhoto.index + 1))}
                  className="max-h-[82vh] w-full rounded-lg bg-background object-contain shadow-2xl"
                />
              )}
            </DialogContent>
          </Dialog>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} /> {t('deliveryDetail.route')}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <div className="text-muted-foreground">{t('deliveryDetail.departure')}</div>
                  <div className="font-medium">{delivery.departure_city}, {delivery.departure_country}</div>
                  {delivery.departure_address && <div className="text-xs text-muted-foreground mt-0.5">{delivery.departure_address}</div>}
                </div>
                <div>
                  <div className="text-muted-foreground">{t('deliveryDetail.arrival')}</div>
                  <div className="font-medium">{delivery.arrival_city}, {delivery.arrival_country}</div>
                  {delivery.arrival_address && <div className="text-xs text-muted-foreground mt-0.5">{delivery.arrival_address}</div>}
                </div>
                <div>
                  <div className="text-muted-foreground">{t('deliveryDetail.desiredDate')}</div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{formatDate(delivery.desired_date)}</div>
                    {isOwner && ['open', 'expired'].includes(delivery.status) && (
                      <button
                        type="button"
                        onClick={openEditDialog}
                        className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-0.5 underline"
                      >
                        <Edit3 size={12} /> {t('deliveryDetail.edit')}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t('deliveryDetail.budget')}</div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-primary">{formatPrice(delivery.budget)}</span>
                    {isOwner && ['open', 'expired'].includes(delivery.status) && (
                      <button
                        type="button"
                        onClick={openEditDialog}
                        className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-0.5 underline"
                      >
                        <Edit3 size={12} /> {t('deliveryDetail.edit')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <MapPanel departure={delivery.departure_city} arrival={delivery.arrival_city} height="260px" />
            </CardContent>
          </Card>

          {(delivery.pickup_start_time || delivery.delivery_start_time) && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('deliveryDetail.appointments')}</CardTitle></CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                {delivery.pickup_start_time && (
                  <div>
                    <div className="text-muted-foreground">{t('deliveryDetail.pickup')}</div>
                    <div className="font-medium">{t('deliveryDetail.dateRange').replace('{start}', formatDateTime(delivery.pickup_start_time)).replace('{end}', formatDateTime(delivery.pickup_end_time))}</div>
                  </div>
                )}
                {delivery.delivery_start_time && (
                  <div>
                    <div className="text-muted-foreground">{t('deliveryDetail.handoff')}</div>
                    <div className="font-medium">{t('deliveryDetail.dateRange').replace('{start}', formatDateTime(delivery.delivery_start_time)).replace('{end}', formatDateTime(delivery.delivery_end_time))}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isInternational && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('deliveryDetail.international')}</AlertTitle>
              <AlertDescription>
                {delivery.customs_declaration ? t('deliveryDetail.customsPrefix').replace('{declaration}', delivery.customs_declaration) : t('deliveryDetail.customsReminder')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">{t('deliveryDetail.sender')}</CardTitle></CardHeader>
            <CardContent>
              <Link to={`/users/${delivery.sender_id}`} className="flex items-center gap-3 mb-3 group">
                <Avatar className="group-hover:ring-2 group-hover:ring-primary transition-shadow">
                  <AvatarImage src={senderProfile?.avatar_url || ''} />
                  <AvatarFallback className="bg-secondary">{(delivery.sender_name || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold group-hover:text-primary group-hover:underline flex items-center gap-1.5">
                    {delivery.sender_name}
                    {senderProfile?.deliverer_validated && <BadgeCheck size={14} className="text-emerald-600" />}
                  </div>
                  {delivery.sender_rating > 0 && (
                    <div className="text-xs text-muted-foreground flex items-center gap-0.5">
                      <Star size={11} className="fill-amber-500 text-amber-500" /> {delivery.sender_rating.toFixed(1)}
                      {senderProfile?.review_count > 0 && ` (${t('deliveryDetail.reviewCount').replace('{count}', String(senderProfile.review_count))})`}
                    </div>
                  )}
                  {(senderProfile?.city || senderProfile?.country) && (
                    <div className="text-xs text-muted-foreground">{[senderProfile.city, senderProfile.country].filter(Boolean).join(', ')}</div>
                  )}
                </div>
              </Link>
              {!isOwner && (
                <Button variant="outline" className="w-full" onClick={() => navigate(`/messages/${delivery.sender_id}`)}>
                  <MessageCircle size={14} className="mr-1" /> {t('deliveryDetail.contact')}
                </Button>
              )}
            </CardContent>
          </Card>

          {!isOwner && delivery.status === 'open' && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('deliveryDetail.offerCardTitle')}</CardTitle></CardHeader>
              <CardContent>
                <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" data-testid="public-parcel-offer-button">{t('deliveryDetail.offerButton')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t('deliveryDetail.offerDialogTitle')}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      {trips.length > 0 && (
                        <div>
                          <Label>{t('deliveryDetail.linkTrip')}</Label>
                          <select className="w-full border rounded-md p-2 text-sm" value={selectedTripId} onChange={e => setSelectedTripId(e.target.value)} data-testid="offer-trip-select">
                            <option value="">{t('deliveryDetail.none')}</option>
                            {trips.map(tripOption => <option key={tripOption.id} value={tripOption.id}>{tripOption.departure_city} → {tripOption.arrival_city} ({formatDate(tripOption.departure_date)})</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <Label>{t('deliveryDetail.proposedPrice')}</Label>
                        <Input type="number" value={offerPrice} onChange={e => setOfferPrice(e.target.value)} placeholder={String(delivery.budget)} data-testid="offer-price-input" />
                      </div>
                      <div>
                        <Label>{t('deliveryDetail.messageOptional')}</Label>
                        <Textarea value={offerMessage} onChange={e => setOfferMessage(e.target.value)} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOfferOpen(false)}>{t('deliveryDetail.cancel')}</Button>
                      <Button onClick={submitOffer} data-testid="offer-submit-button">{t('deliveryDetail.send')}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}

          {isOwner && ['open', 'expired'].includes(delivery.status) && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('deliveryDetail.manage')}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={openEditDialog}
                  data-testid="edit-delivery-button"
                >
                  <Edit3 size={14} /> {t(delivery.status === 'expired' ? 'deliveryDetail.reopen' : 'deliveryDetail.editInfo')}
                </Button>
                {delivery.status === 'open' && (
                  <Button variant="destructive" className="w-full" onClick={cancelDelivery} data-testid="cancel-delivery-button">
                    {t('deliveryDetail.cancelParcel')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isOwner && isPublishedToday && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('deliveryDetail.permanentDelete')}</CardTitle></CardHeader>
              <CardContent>
                <Button
                  variant="destructive"
                  className="w-full gap-1.5"
                  onClick={deleteDeliveryPermanently}
                  data-testid="delete-delivery-permanent-button"
                >
                  <Trash2 size={14} /> {t('deliveryDetail.deleteForever')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Dialog edit parcel */}
          {isOwner && (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('deliveryDetail.editTitle')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveEdit} className="space-y-4 pt-2">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label htmlFor="edit-title">{t('deliveryDetail.parcelTitle')}</Label>
                      <Input
                        id="edit-title"
                        required
                        value={editForm.title || ''}
                        onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="edit-description">{t('deliveryDetail.description')}</Label>
                      <Textarea
                        id="edit-description"
                        value={editForm.description || ''}
                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="edit-delivery-photos">{t('deliveryDetail.photos')}</Label>
                      <div className="flex flex-wrap gap-3">
                        {(editForm.photos || []).map((photo, index) => (
                          <div key={photo} className="relative h-24 w-24 overflow-hidden rounded-md border bg-muted">
                            <img src={photo} alt={t('deliveryDetail.photoAlt').replace('{index}', String(index + 1))} className="h-full w-full object-cover" />
                            <Button type="button" size="icon" variant="destructive" className="absolute right-1 top-1 h-6 w-6" onClick={() => removeEditPhoto(index)} aria-label={t('deliveryDetail.removePhoto').replace('{index}', String(index + 1))}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        ))}
                        {(editForm.photos || []).length < MAX_PHOTOS && (
                          <label htmlFor="edit-delivery-photos" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground hover:border-primary hover:text-primary">
                            <ImagePlus size={22} />
                            <span className="text-xs">{t('deliveryDetail.add')}</span>
                          </label>
                        )}
                      </div>
                      <Input id="edit-delivery-photos" type="file" accept="image/*" multiple className="sr-only" onChange={addEditPhotos} disabled={photosProcessing || (editForm.photos || []).length >= MAX_PHOTOS} data-testid="edit-delivery-photos-input" />
                      <p className="text-xs text-muted-foreground">{t('deliveryDetail.photosLimit').replace('{count}', String(MAX_PHOTOS))}</p>
                    </div>
                    <div>
                      <Label htmlFor="edit-date">{t('deliveryDetail.desiredDate')}</Label>
                      <Input
                        id="edit-date"
                        type="date"
                        value={editForm.desired_date || ''}
                        onChange={e => setEditForm(f => ({ ...f, desired_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-pickup-start">{t('deliveryDetail.pickupFrom')}</Label>
                      <Input id="edit-pickup-start" type="datetime-local" value={editForm.pickup_start_time || ''} onChange={e => setEditForm(f => ({ ...f, pickup_start_time: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="edit-pickup-end">{t('deliveryDetail.pickupUntil')}</Label>
                      <Input id="edit-pickup-end" type="datetime-local" value={editForm.pickup_end_time || ''} onChange={e => setEditForm(f => ({ ...f, pickup_end_time: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="edit-delivery-start">{t('deliveryDetail.handoffFrom')}</Label>
                      <Input id="edit-delivery-start" type="datetime-local" value={editForm.delivery_start_time || ''} onChange={e => setEditForm(f => ({ ...f, delivery_start_time: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="edit-delivery-end">{t('deliveryDetail.handoffUntil')}</Label>
                      <Input id="edit-delivery-end" type="datetime-local" value={editForm.delivery_end_time || ''} onChange={e => setEditForm(f => ({ ...f, delivery_end_time: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="edit-budget">{t('deliveryDetail.proposedBudget')}</Label>
                      <Input
                        id="edit-budget"
                        type="number"
                        step="0.5"
                        min="1"
                        required
                        value={editForm.budget || ''}
                        onChange={e => setEditForm(f => ({ ...f, budget: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-type">{t('deliveryDetail.parcelType')}</Label>
                      <select
                        id="edit-type"
                        value={editForm.parcel_type || 'small'}
                        onChange={e => setEditForm(f => ({ ...f, parcel_type: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm mt-1 bg-background text-foreground"
                      >
                        {PARCEL_TYPES.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-size">{t('deliveryDetail.size')}</Label>
                      <select
                        id="edit-size"
                        value={editForm.size || 'S'}
                        onChange={e => setEditForm(f => ({ ...f, size: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm mt-1 bg-background text-foreground"
                      >
                        <option value="S">{t('deliveryDetail.sizeS')}</option>
                        <option value="M">{t('deliveryDetail.sizeM')}</option>
                        <option value="L">{t('deliveryDetail.sizeL')}</option>
                        <option value="XL">{t('deliveryDetail.sizeXL')}</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-weight">{t('deliveryDetail.weight')}</Label>
                      <Input
                        id="edit-weight"
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={editForm.weight_kg || ''}
                        onChange={e => setEditForm(f => ({ ...f, weight_kg: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-dimensions">{t('deliveryDetail.dimensions')}</Label>
                      <Input
                        id="edit-dimensions"
                        placeholder="30x20x15"
                        value={editForm.dimensions_cm || ''}
                        onChange={e => setEditForm(f => ({ ...f, dimensions_cm: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-rec-name">{t('deliveryDetail.recipientName')}</Label>
                      <Input
                        id="edit-rec-name"
                        value={editForm.recipient_name || ''}
                        onChange={e => setEditForm(f => ({ ...f, recipient_name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-rec-phone">{t('deliveryDetail.recipientPhone')}</Label>
                      <Input
                        id="edit-rec-phone"
                        value={editForm.recipient_phone || ''}
                        onChange={e => setEditForm(f => ({ ...f, recipient_phone: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-dep-address">{t('deliveryDetail.pickupAddress')}</Label>
                      <Input
                        id="edit-dep-address"
                        value={editForm.departure_address || ''}
                        onChange={e => setEditForm(f => ({ ...f, departure_address: e.target.value }))}
                        placeholder={t('deliveryDetail.addressPlaceholder')}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-arr-address">{t('deliveryDetail.deliveryAddress')}</Label>
                      <Input
                        id="edit-arr-address"
                        value={editForm.arrival_address || ''}
                        onChange={e => setEditForm(f => ({ ...f, arrival_address: e.target.value }))}
                        placeholder={t('deliveryDetail.addressPlaceholder')}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-4 pt-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="edit-fragile"
                          checked={!!editForm.fragile}
                          onCheckedChange={v => setEditForm(f => ({ ...f, fragile: !!v }))}
                        />
                        <Label htmlFor="edit-fragile" className="cursor-pointer text-sm">{t('deliveryDetail.fragile')}</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="edit-urgent"
                          checked={!!editForm.urgent}
                          onCheckedChange={v => setEditForm(f => ({ ...f, urgent: !!v }))}
                        />
                        <Label htmlFor="edit-urgent" className="cursor-pointer text-sm">{t('deliveryDetail.urgent')}</Label>
                      </div>
                    </div>
                  </div>
                  <DialogFooter className="pt-3">
                    <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                      {t('deliveryDetail.cancel')}
                    </Button>
                    <Button type="submit" disabled={savingEdit}>
                      {savingEdit ? t('deliveryDetail.saving') : t('deliveryDetail.saveChanges')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
